// Invoice service using Supabase client directly
import { Invoice } from '@/shared/types';
import { authService } from '@/shared/services/realData/auth';

// Helper function to map job status to invoice status
function mapJobStatusToInvoiceStatus(jobStatus: string): 'draft' | 'pending' | 'paid' | 'overdue' {
  const statusMap: Record<string, 'draft' | 'pending' | 'paid' | 'overdue'> = {
    'draft': 'draft',
    'pending': 'pending',
    'in_progress': 'pending',
    'completed': 'paid',
    'paid': 'paid',
    'overdue': 'overdue',
    'cancelled': 'draft'
  };
  return statusMap[jobStatus] || 'draft';
}

// Helper function to map invoice status to job status
function mapInvoiceStatusToJobStatus(invoiceStatus: string): string {
  const statusMap: Record<string, string> = {
    'draft': 'draft',
    'pending': 'pending',
    'paid': 'completed',
    'overdue': 'overdue'
  };
  return statusMap[invoiceStatus] || 'draft';
}

export const invoiceService = {
  getInvoices: async (): Promise<Invoice[]> => {
    // Get the current user's Supabase ID
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) {
      return [];
    }

    // Import SupabaseService dynamically
    const { supabaseService } = await import('@/shared/services/supabaseService');

    // Fetch jobs from the database (invoices are stored as jobs)
    const { data: jobs, error } = await supabaseService.client
      .from('jobs')
      .select('*')
      .eq('user_id', supabaseUserId)
      .order('created_at', { ascending: false });

    if (error || !jobs) {
      console.error('Error fetching invoices:', error);
      return [];
    }

    // Convert jobs to invoice format
    return jobs.map((job: any) => ({
      id: job.id,
      user_id: job.user_id,
      userId: job.user_id,
      client_id: job.client_id,
      clientId: job.client_id,
      invoice_number: `INV-${job.id.slice(-6)}`,
      invoiceNumber: `INV-${job.id.slice(-6)}`,
      title: job.title,
      description: job.description || null,
      status: mapJobStatusToInvoiceStatus(job.status),
      currency: 'USD' as const,
      subtotal: job.total_amount || 0,
      tax_rate: 0,
      taxRate: 0,
      tax_amount: 0,
      taxAmount: 0,
      total_amount: job.total_amount || 0,
      totalAmount: job.total_amount || 0,
      paid_amount: job.amount_paid || 0,
      amountPaid: job.amount_paid || 0,
      issue_date: job.created_at || null,
      dateIssued: job.created_at || null,
      due_date: job.due_date || null,
      dueDate: job.due_date || null,
      notes: job.notes || null,
      terms: null,
      public_token: null,
      created_at: job.created_at,
      createdAt: job.created_at,
      updated_at: job.updated_at || job.created_at,
      updatedAt: job.updated_at || job.created_at,
      items: (job.services || []).map((service: any) => ({
        id: service.id,
        invoice_id: job.id,
        invoiceId: job.id,
        service_id: service.id,
        serviceId: service.id,
        name: service.name,
        description: service.description || null,
        quantity: 1,
        unit_price: service.rate,
        rate: service.rate,
        total_price: service.rate,
        amount: service.rate,
        order_index: null,
        created_at: job.created_at,
        createdAt: job.created_at
      }))
    }));
  },

  createInvoice: async (invoiceData: Partial<Invoice>): Promise<Invoice> => {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) {
      throw new Error('User not authenticated or profile not found');
    }

    const items = invoiceData.items || [];
    const newInvoice: Invoice = {
      id: crypto.randomUUID(),
      user_id: supabaseUserId,
      userId: supabaseUserId,
      clientId: invoiceData.clientId || '',
      client_id: invoiceData.clientId || '',
      invoiceNumber: invoiceData.invoiceNumber || `INV-${Date.now()}`,
      invoice_number: invoiceData.invoiceNumber || `INV-${Date.now()}`,
      title: invoiceData.title || '',
      description: invoiceData.description || null,
      status: invoiceData.status || 'draft',
      currency: invoiceData.currency || 'KES',
      subtotal: invoiceData.subtotal || 0,
      taxRate: invoiceData.taxRate || 0,
      tax_rate: invoiceData.taxRate || 0,
      taxAmount: invoiceData.taxAmount || 0,
      tax_amount: invoiceData.taxAmount || 0,
      totalAmount: invoiceData.totalAmount || 0,
      total_amount: invoiceData.totalAmount || 0,
      amountPaid: invoiceData.amountPaid || 0,
      paid_amount: invoiceData.amountPaid || 0,
      dateIssued: invoiceData.dateIssued || new Date().toISOString(),
      issue_date: invoiceData.dateIssued || new Date().toISOString(),
      dueDate: invoiceData.dueDate || new Date().toISOString(),
      due_date: invoiceData.dueDate || new Date().toISOString(),
      notes: invoiceData.notes || null,
      terms: invoiceData.terms || null,
      public_token: null,
      logoUrl: invoiceData.logoUrl || null,
      createdAt: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      items: items
    };

    // Import SupabaseService dynamically
    const { supabaseService } = await import('@/shared/services/supabaseService');

    // For now, store as a job in the database (until we have proper invoice table)
    const jobData = {
      id: newInvoice.id,
      user_id: supabaseUserId,
      userId: supabaseUserId, // Keep for backward compatibility
      client_id: newInvoice.clientId,
      clientId: newInvoice.clientId,
      title: newInvoice.title,
      description: newInvoice.description,
      status: mapInvoiceStatusToJobStatus(newInvoice.status),
      total_amount: newInvoice.totalAmount,
      totalAmount: newInvoice.totalAmount,
      amount_paid: 0,
      amountPaid: 0,
      due_date: newInvoice.dueDate,
      dueDate: newInvoice.dueDate,
      created_at: newInvoice.createdAt,
      createdAt: newInvoice.createdAt,
      services: newInvoice.items.map(item => ({
        id: crypto.randomUUID(),
        name: item.name || item.description,
        description: item.description || '',
        rate: item.rate || 0,
        quantity: item.quantity || 1
      }))
    };

    const { error } = await supabaseService.client
      .from('jobs')
      .insert(jobData);

    if (error) {
      console.error('Error creating invoice:', error);
      throw error;
    }

    return newInvoice;
  }
};
